<template>
  <form
    v-on:submit.prevent="onSubmit"
    class="form-system-clinic-document-template">
    <p>
      Чтобы отредактировать {{ initialTemplate.name }} скачайте шаблон,
      внесите изменения и загрузите файл в МИС
    </p>
    <div class="form-system-clinic-document-template__buttons">
      <button
        type="button"
        class="form-system-clinic-document-template__buttons-download-template"
        v-on:click="onClickDownloadSystemTemplate"
      >
        Скачать шаблон
      </button>
      <button
        type="button"
        class="form-system-clinic-document-template__buttons-download-variables"
        v-on:click="onClickDownloadVariables"
      >
        Скачать переменные
      </button>
    </div>
    <div>
      <input-file
        title="Файл шаблона"
        required
        v-bind:with-view-button="false"
        v-bind:accept="$options.acceptList"
        v-bind:violations="fileInputErrors"
        v-on:download-file="onDownloadFile"
        v-model="file"
      />
      <p class="form-system-clinic-document-template__addiction-info">не более 10мб, файлы .docx</p>
    </div>
    <div class="form-system-clinic-document-template__buttons">
      <button
        class="form-system-clinic-document-template__buttons-cancel"
        v-on:click="() => $emit('cancel')"
        type="button">Отмена</button>
      <button
        class="form-system-clinic-document-template__buttons-submit"
        v-bind:disabled="disableUpload"
        type="submit">Сохранить</button>
    </div>
  </form>
</template>

<script>
import InputFile from '@/components/InputFile.vue';
import {
  ClinicSystemTemplateRequestEdit,
} from '@/storage/gateways/DocumentTemplateGateway';
import clinicTemplateMixin from '@/mixins/clinicTemplateMixin';
import notifierMixin from '@/mixins/notifierMixin';

/**
 * @description Название события отправки договора.
 * @type {string}
 */
const NAME_SUBMIT_EVENT = 'submit';

/**
 * @description Название события "Скачать переменные".
 * @type {string}
 */
const NAME_DOWNLOAD_VARIABLES_EVENT = 'download-variables';

/**
 * @description Название события "Скачать переменные".
 * @type {string}
 */
const NAME_DOWNLOAD_SYSTEM_TEMPLATE_EVENT = 'download-system-template';

/**
 * @description Название события загрузки файла.
 * @type {string}
 */
const NAME_DOWNLOAD_FILE_EVENT = 'download-file';

const acceptList = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.docx',
  'application/vnd.oasis.opendocument.text',
  '.odt',
].join(', ');

const MAX_SIZE_FILE = 1024 * 1024 * 100;

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

export default {
  /**
    * @description Компонент формы редактирования шаблона документа клиники.
    */
  name: 'FormEditClinicDocumentTemplate',
  components: {
    InputFile,
  },
  acceptList,
  computed: {
    disableUpload: ({ isAwaitRequest }) => isAwaitRequest,
    fileInputErrors: resolveInputErrors('file'),
  },
  methods: {
    /**
     * @description Обработать событие нажатия на кнопку "Скачать переменные".
     * @returns {void}
     */
    onClickDownloadVariables() {
      this.$emit(NAME_DOWNLOAD_VARIABLES_EVENT, this.type);
    },
    /**
     * @description Обработать событие нажатия на кнопку "Скачать шаблон".
     * @returns {void}
     */
    onClickDownloadSystemTemplate() {
      this.$emit(NAME_DOWNLOAD_SYSTEM_TEMPLATE_EVENT, this.type);
    },
    /**
     * @description Проверить файл.
     * @returns {boolean}
     */
    validateFile() {
      if (this.file && this.file.size > MAX_SIZE_FILE) {
        this.notifyFail('Файл сильно большой.');
        return false;
      }
      return true;
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      if (!this.validateFile()) {
        return;
      }
      this.$emit(
        NAME_SUBMIT_EVENT,
        new ClinicSystemTemplateRequestEdit(
          this.initialTemplate.id,
          this.name,
          this.file,
          this.type,
        ),
      );
    },
    /**
     * @description Обработать событие загрузки файла.
     * @returns {void}
     */
    onDownloadFile() {
      this.$emit(NAME_DOWNLOAD_FILE_EVENT, {
        file: this.file,
        type: this.type,
      });
    },
  },
  data() {
    return {
      /**
       * @description Id категории.
       * @type {string}
       */
      categoryId: this.initialTemplate.categoryId,
      /**
       * @description Файл.
       * @type {Object|File}
       */
      file: this.initialTemplate.fileName
        ? {
          name: this.initialTemplate.fileName,
          id: this.initialTemplate.id,
        } : null,
      /**
       * @description Тип документа.
       * @type {String}
       */
      type: this.initialTemplate.type,
    };
  },
  props: {
    /**
     * @description Нарушения ограничений.
     * @type {any}
     */
    inputErrors: { type: Object, required: true },
    /**
     * @description Начальный шаблон
     * @type {Template}
     */
    initialTemplate: { type: Object, required: true },
  },
  mixins: [
    clinicTemplateMixin,
    notifierMixin,
  ],
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.form-system-clinic-document-template {
  display: grid;
  gap: 10px;
  padding-block: 20px;
  &__addiction-info {
    color: gray;
    font-size: 12px;
  }
  &__buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    max-width: 438px;
    width: 100%;
    margin: 10px 0 0 auto;
    &-submit {
      @include buttons.btn-blue(100%, 3rem, 1rem);
      &:disabled {
        background-color: var(--unfocus-color);
      }
    }
    &-cancel {
      @include buttons.btn-transparent(100%, 3rem, 1rem);
    }
    &-download-template {
      @include buttons.btn-blue(100%, 3rem, 1rem);
    }
    &-download-variables {
      @include buttons.btn-transparent(100%, 3rem, 1rem);
    }
  }
}
</style>
